const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/v1';

// Custom Error Class
class ApiError extends Error {
  constructor(message, status, data = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
    this.timestamp = new Date().toISOString();
  }
}

// Error Handler
const handleApiError = (error, endpoint) => {
  // Network errors
  if (error instanceof TypeError && error.message === 'Failed to fetch') {
    console.error(`❌ Network Error on ${endpoint}:`, 'No internet connection or server is down');
    throw new ApiError('No internet connection. Please check your network.', 0);
  }

  // API errors
  if (error instanceof ApiError) {
    console.error(`❌ API Error on ${endpoint}:`, {
      message: error.message,
      status: error.status,
      data: error.data,
      timestamp: error.timestamp
    });
    throw error;
  }

  // Unknown errors
  console.error(`❌ Unknown Error on ${endpoint}:`, error);
  throw new ApiError('An unexpected error occurred. Please try again.', 500);
};

// Response Handler with Detailed Error Handling
const handleResponse = async (response, endpoint) => {
  let data;
  
  // Try to parse JSON
  try {
    data = await response.json();
  } catch (parseError) {
    // If JSON parsing fails
    if (!response.ok) {
      throw new ApiError(
        `Server returned ${response.status} ${response.statusText}`,
        response.status
      );
    }
    throw new ApiError('Invalid response format from server', response.status);
  }

  // Handle different status codes
  if (!response.ok) {
    const errorMessage = data.message || data.error || `Request failed with status ${response.status}`;
    
    switch (response.status) {
      case 400:
        throw new ApiError(`Bad Request: ${errorMessage}`, 400, data);
      case 401:
        // Unauthorized - Clear session
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');

        // Mark user as logged out
        localStorage.setItem('isAuthenticated', 'false');

        // Optional: redirect to login page
        window.location.href = '/';

        throw new ApiError(
            'Session expired. Please login again.',
            401,
            data
        );
      case 403:
        throw new ApiError('Access denied. You do not have permission.', 403, data);
      case 404:
        throw new ApiError(`Not found: ${errorMessage}`, 404, data);
      case 422:
        throw new ApiError(`Validation Error: ${errorMessage}`, 422, data);
      case 429:
        throw new ApiError('Too many requests. Please try again later.', 429, data);
      case 500:
        throw new ApiError('Server error. Please try again later.', 500, data);
      case 503:
        throw new ApiError('Service unavailable. Please try again later.', 503, data);
      default:
        throw new ApiError(errorMessage, response.status, data);
    }
  }

  return data;
};

// Get auth token
const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

// Create headers
const createHeaders = (includeAuth = true, isFormData = false) => {
  const headers = {};
  
  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }
  
  if (includeAuth) {
    const token = getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }
  
  return headers;
};

// Request Logger (for debugging)
const logRequest = (method, endpoint, data = null) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(`🚀 API ${method} Request:`, {
      endpoint: `${API_BASE_URL}/${endpoint}`,
      data: data,
      timestamp: new Date().toISOString()
    });
  }
};

// Response Logger (for debugging)
const logResponse = (method, endpoint, data) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(`✅ API ${method} Response:`, {
      endpoint: `${API_BASE_URL}/${endpoint}`,
      data: data,
      timestamp: new Date().toISOString()
    });
  }
};

// Main API Service
const apiService = {
  
  // ==================== GET METHOD ====================
  get: async (endpoint, options = {}) => {
    const { includeAuth = true } = options;
    
    try {
      logRequest('GET', endpoint);
      
      const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
        method: 'GET',
        headers: createHeaders(includeAuth, false),
      });
      
      const data = await handleResponse(response, endpoint);
      logResponse('GET', endpoint, data);
      
      return data;
    } catch (error) {
      handleApiError(error, endpoint);
    }
  },
  
  // ==================== POST METHOD (JSON) ====================
  post: async (endpoint, requestData, options = {}) => {
    const { includeAuth = true } = options;
    
    try {
      logRequest('POST', endpoint, requestData);
      
      const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
        method: 'POST',
        headers: createHeaders(includeAuth, false),
        body: JSON.stringify(requestData),
      });
      
      const data = await handleResponse(response, endpoint);
      logResponse('POST', endpoint, data);
      
      return data;
    } catch (error) {
      handleApiError(error, endpoint);
    }
  },
  
  // ==================== PUT METHOD ====================
  put: async (endpoint, requestData, options = {}) => {
    const { includeAuth = true } = options;
    
    try {
      logRequest('PUT', endpoint, requestData);
      
      const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
        method: 'PUT',
        headers: createHeaders(includeAuth, false),
        body: JSON.stringify(requestData),
      });
      
      const data = await handleResponse(response, endpoint);
      logResponse('PUT', endpoint, data);
      
      return data;
    } catch (error) {
      handleApiError(error, endpoint);
    }
  },
  
  // ==================== PATCH METHOD ====================
  patch: async (endpoint, requestData, options = {}) => {
    const { includeAuth = true } = options;
    
    try {
      logRequest('PATCH', endpoint, requestData);
      
      const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
        method: 'PATCH',
        headers: createHeaders(includeAuth, false),
        body: JSON.stringify(requestData),
      });
      
      const data = await handleResponse(response, endpoint);
      logResponse('PATCH', endpoint, data);
      
      return data;
    } catch (error) {
      handleApiError(error, endpoint);
    }
  },
  
  // ==================== DELETE METHOD ====================
  delete: async (endpoint, options = {}) => {
    const { includeAuth = true } = options;
    
    try {
      logRequest('DELETE', endpoint);
      
      const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
        method: 'DELETE',
        headers: createHeaders(includeAuth, false),
      });
      
      const data = await handleResponse(response, endpoint);
      logResponse('DELETE', endpoint, data);
      
      return data;
    } catch (error) {
      handleApiError(error, endpoint);
    }
  },
  
  // ==================== POST METHOD (FormData) ====================
  postFormData: async (endpoint, formData, options = {}) => {
    const { includeAuth = true, onProgress = null } = options;
    
    try {
      logRequest('POST FormData', endpoint, 'FormData object');
      
      // Create XMLHttpRequest for progress tracking
      if (onProgress) {
        return new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          
          // Progress event
          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
              const percentComplete = Math.round((e.loaded / e.total) * 100);
              onProgress(percentComplete);
            }
          });
          
          // Load event
          xhr.addEventListener('load', async () => {
            try {
              const data = JSON.parse(xhr.responseText);
              if (xhr.status >= 200 && xhr.status < 300) {
                logResponse('POST FormData', endpoint, data);
                resolve(data);
              } else {
                const error = new ApiError(
                  data.message || `Request failed with status ${xhr.status}`,
                  xhr.status,
                  data
                );
                reject(error);
              }
            } catch (error) {
              reject(error);
            }
          });
          
          // Error event
          xhr.addEventListener('error', () => {
            const error = new ApiError('Network error occurred', 0);
            reject(error);
          });
          
          // Setup and send
          xhr.open('POST', `${API_BASE_URL}/${endpoint}`);
          
          const token = getAuthToken();
          if (includeAuth && token) {
            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          }
          
          xhr.send(formData);
        });
      }
      
      // Regular fetch for FormData without progress
      const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
        method: 'POST',
        headers: createHeaders(includeAuth, true),
        body: formData,
      });
      
      const data = await handleResponse(response, endpoint);
      logResponse('POST FormData', endpoint, data);
      
      return data;
    } catch (error) {
      handleApiError(error, endpoint);
    }
  },
  
  // ==================== PUT METHOD (FormData) ====================
  putFormData: async (endpoint, formData, options = {}) => {
    const { includeAuth = true, onProgress = null } = options;
    
    try {
      logRequest('PUT FormData', endpoint, 'FormData object');
      
      // Create XMLHttpRequest for progress tracking
      if (onProgress) {
        return new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          
          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
              const percentComplete = Math.round((e.loaded / e.total) * 100);
              onProgress(percentComplete);
            }
          });
          
          xhr.addEventListener('load', async () => {
            try {
              const data = JSON.parse(xhr.responseText);
              if (xhr.status >= 200 && xhr.status < 300) {
                logResponse('PUT FormData', endpoint, data);
                resolve(data);
              } else {
                const error = new ApiError(
                  data.message || `Request failed with status ${xhr.status}`,
                  xhr.status,
                  data
                );
                reject(error);
              }
            } catch (error) {
              reject(error);
            }
          });
          
          xhr.addEventListener('error', () => {
            const error = new ApiError('Network error occurred', 0);
            reject(error);
          });
          
          xhr.open('PUT', `${API_BASE_URL}/${endpoint}`);
          
          const token = getAuthToken();
          if (includeAuth && token) {
            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          }
          
          xhr.send(formData);
        });
      }
      
      // Regular fetch for FormData without progress
      const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
        method: 'PUT',
        headers: createHeaders(includeAuth, true),
        body: formData,
      });
      
      const data = await handleResponse(response, endpoint);
      logResponse('PUT FormData', endpoint, data);
      
      return data;
    } catch (error) {
      handleApiError(error, endpoint);
    }
  },
  
  // ==================== HELPER METHODS ====================
  
  // Check if user is authenticated
  isAuthenticated: () => {
    return !!getAuthToken();
  },
  
  // Get current user from localStorage
  getCurrentUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },
  
  // Logout user
  logout: () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    return true;
  },
  
  // Set auth token
  setAuthToken: (token) => {
    localStorage.setItem('authToken', token);
  },
  
  // Set user data
  setUser: (user) => {
    localStorage.setItem('user', JSON.stringify(user));
  },
  
  // Get auth headers (useful for custom requests)
  getAuthHeaders: () => {
    return createHeaders(true, false);
  },
  
  // Get API base URL
  getBaseURL: () => {
    return API_BASE_URL;
  }
};

export default apiService;
export { ApiError };